// src/utils.js

/**
 * Formats various date inputs into a YYYY-MM-DD string.
 * Handles ISO strings, JS Date objects, and Neo4j Date objects.
 * @param {*} dateValue - The date value to format.
 * @returns {string|null} Formatted date string or null if input is null/undefined.
 */
export const formatDate = (dateValue) => {
    if (!dateValue) return null;

    // Handle YYYY-MM-DD strings directly
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        return dateValue;
    }

    // Handle JS Date objects
    if (dateValue instanceof Date) {
        try {
            // Ensure date is valid before formatting
            if (isNaN(dateValue.getTime())) return String(dateValue); // Return 'Invalid Date' string representation
            return dateValue.toISOString().split('T')[0];
        } catch (e) {
            console.warn("Error formatting JS Date:", dateValue, e);
            return String(dateValue); // Fallback
        }
    }

    // Handle Neo4j Date objects (Structure: { year, month, day })
    // Use optional chaining and check for all properties
    if (typeof dateValue === 'object' && dateValue?.year !== undefined && dateValue?.month !== undefined && dateValue?.day !== undefined && dateValue?.hour === undefined) {
        try {
            const y = Number(dateValue.year);
            const m = Number(dateValue.month);
            const d = Number(dateValue.day);
            if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
                return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            }
        } catch (e) {
             console.warn("Error formatting Neo4j Date object:", dateValue, e);
        }
    }

    // Handle DateTime strings (ISO format) or Neo4j DateTime objects by converting to JS Date
    // Neo4j DateTime objects might be automatically converted by the driver or have a similar structure to Neo4j Date + time components
    try {
        const date = new Date(dateValue);
        if (!isNaN(date.getTime())) {
            // If it looks like just a date (time is midnight UTC), format as date only
            if (date.toISOString().endsWith('T00:00:00.000Z') && typeof dateValue !== 'string') {
                 return date.toISOString().split('T')[0];
            }
             // Otherwise, attempt robust date-only format (might not be perfect for all locales)
            // return date.toLocaleDateString('en-CA'); // 'en-CA' often gives YYYY-MM-DD
            // Safer fallback: return the ISO date part
             return date.toISOString().split('T')[0];

        }
    } catch (e) {
        console.warn("Could not parse as Date for formatting:", dateValue, e);
    }

    console.warn("Unrecognized value passed to formatDate:", dateValue);
    return String(dateValue); // Fallback to string representation
};


/**
 * Formats various date/datetime inputs into a locale-specific date and time string.
 * Handles ISO strings, JS Date objects, and Neo4j DateTime objects.
 * @param {*} dateTimeValue - The date/time value to format.
 * @returns {string|null} Formatted date/time string or null if input is null/undefined.
 */
export const formatDateTime = (dateTimeValue) => {
    if (!dateTimeValue) return null;

    try {
        // Attempt to create a JS Date object from the input
        // This handles ISO strings, JS Date objects, and Neo4j DateTime objects if parsed correctly by driver/logic
        const date = new Date(dateTimeValue);

        // Check if the date object is valid
        if (isNaN(date.getTime())) {
            // Handle Neo4j DateTime objects manually if needed (check structure)
            if (typeof dateTimeValue === 'object' && dateTimeValue?.year !== undefined && dateTimeValue?.month !== undefined && dateTimeValue?.day !== undefined && dateTimeValue?.hour !== undefined) {
                 // Reconstruct ISO-like string for Date constructor
                 const isoString = `${String(dateTimeValue.year).padStart(4, '0')}-${String(dateTimeValue.month).padStart(2, '0')}-${String(dateTimeValue.day).padStart(2, '0')}T${String(dateTimeValue.hour).padStart(2, '0')}:${String(dateTimeValue.minute ?? 0).padStart(2, '0')}:${String(dateTimeValue.second ?? 0).padStart(2, '0')}`;
                 const reconstructedDate = new Date(isoString);
                 if (!isNaN(reconstructedDate.getTime())) {
                    return reconstructedDate.toLocaleString([], { // Use default locale
                        year: 'numeric', month: 'numeric', day: 'numeric',
                        hour: 'numeric', minute: '2-digit'
                    });
                 }
            }
            console.warn("Invalid Date object created in formatDateTime:", dateTimeValue);
            return String(dateTimeValue); // Fallback for invalid dates
        }

        // Format the valid date object
        return date.toLocaleString([], { // Use user's default locale settings
            year: 'numeric', month: 'numeric', day: 'numeric',
            hour: 'numeric', minute: '2-digit' // Adjust options as needed
        });
    } catch (e) {
        console.error("Error formatting DateTime:", dateTimeValue, e);
        return String(dateTimeValue); // Fallback to string representation on error
    }
};

export const formatScore = (score) => {
    const numericScore = typeof score === 'number' ? score : parseFloat(score);
    if (isNaN(numericScore)) {
        return <span className="text-gray-400 italic">N/A</span>;
    }
    // Basic color coding - adjust thresholds/colors as needed per score type
    let colorClass = 'bg-gray-100 text-gray-800'; // Default/Neutral
    if (numericScore >= 0.7) {
        colorClass = 'bg-red-100 text-red-800'; // High Priority/Risk/Urgency
    } else if (numericScore >= 0.4) {
        colorClass = 'bg-yellow-100 text-yellow-800'; // Medium
    } else if (numericScore > 0) {
         colorClass = 'bg-green-100 text-green-800'; // Low (but positive)
    }

    return (
        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${colorClass}`}>
            {numericScore.toFixed(3)} {/* Show 3 decimal places */}
        </span>
    );
};

export const verifyUserCredentials = (email, password) => {
    const userCredentials = JSON.parse(process.env.REACT_APP_USER_CREDENTIALS)
    return userCredentials.find((u) => u.email === email && u.password === password)
}

export const isAdminUser = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  if (user.user_type !== "admin") {
    return false
  }
  return true
}
